/**
 * CallMethodSheet — bottom-sheet that lets the user choose between
 * "Call with Service Strike" (Twilio Voice) and "Call via Phone" (native dialer).
 *
 * When `masked` is true (CallMaskingEnabled job, Technician role):
 *  - The native "Regular Phone Call" button is hidden.
 *  - The call is initiated via an opaque contact reference (no real E.164 sent to client).
 *  - `contactId` must be supplied; `toE164` is only used for display (last-4 form).
 *
 * Usage:
 *   <CallMethodSheet
 *     isOpen={open}
 *     onClose={() => setOpen(false)}
 *     toE164="+15551234567"
 *     displayName="John Smith"
 *   />
 *   // Masked:
 *   <CallMethodSheet
 *     isOpen={open}
 *     onClose={() => setOpen(false)}
 *     toE164="••• ••• 1234"
 *     displayName="John Smith"
 *     masked
 *     contactId="some-guid"
 *     contactWhich="primary"
 *     jobId="job-guid"
 *   />
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Phone, X } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { apiService } from '@/lib/api';
import { voiceManager } from '@/lib/voiceManager';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePermissions } from '@/hooks/usePermissions';
import { formatPhoneNumber, formatVoipNumberLabel } from '@/lib/phoneUtils';
import type { PhoneNumberDTO } from '@/types';

interface CallMethodSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** E.164 destination number (or last-4 masked form when masked=true) */
  toE164: string;
  /** Human-readable name to display and pass to the call overlay */
  displayName?: string;
  /** Optional job ID to link the call to after initiation */
  jobId?: string;
  /** When true, hides native call and routes through Twilio with server-side number resolution. */
  masked?: boolean;
  /** Required when masked=true: the contact's UUID used in the opaque reference. */
  contactId?: string;
  /** Which phone number to call when masked. Defaults to "primary". */
  contactWhich?: 'primary' | 'additional';
}

export function CallMethodSheet({ isOpen, onClose, toE164, displayName, jobId, masked, contactId, contactWhich = 'primary' }: CallMethodSheetProps) {
  const { makeCall, makeMaskedCall, callState } = useVoiceCall();
  const [voiceNumbers, setVoiceNumbers] = useState<PhoneNumberDTO[]>([]);
  const [activeProvider, setActiveProvider] = useState<'twilio' | 'telnyx'>('twilio');
  const [loading, setLoading] = useState(false);
  // True once this opening's number list arrived; gates the one-line auto-dial below.
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const autoDialed = useRef(false);
  const [webSupported, setWebSupported] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const isMobile = useIsMobile();
  const { isFeatureAllowed } = usePermissions();

  // The in-browser softphone runs on desktop web only (mobile browser keeps its softphone-less
  // behavior), and only when the active provider is Telnyx. Masked calls are mobile-only. Native
  // always supports the softphone.
  useEffect(() => {
    if (!isOpen || isNative || isMobile || masked) { setWebSupported(false); return; }
    let cancelled = false;
    voiceManager.isSupported().then((ok) => { if (!cancelled) setWebSupported(ok); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, isNative, isMobile, masked]);
  // Calling through a Service Strike number is a plan feature. Without it the sheet still
  // offers the native dialer, so the user can place an ordinary phone call.
  const softphoneSupported = (isNative || webSupported) && isFeatureAllowed('voip.call');

  /**
   * A mobile browser can't run the softphone, so "Regular Phone Call" is the only choice left and the
   * sheet would just be a one-option prompt — hand straight off to the phone's dialer instead. Known
   * synchronously (the check above doesn't even probe on mobile), so there's no risk of jumping the
   * gun while support is still resolving. Desktop keeps the sheet: a tel: link there rarely does
   * anything useful, so silently triggering one would be worse than showing the choice.
   */
  const phoneCallOnly = !isNative && isMobile && !masked;
  useEffect(() => {
    if (!isOpen || !phoneCallOnly) return;
    onClose();
    window.location.href = `tel:${toE164}`;
  }, [isOpen, phoneCallOnly, toE164, onClose]);

  // Fetch the numbers this user may call from when the sheet opens. The server already limits a
  // technician to the numbers technicians can use, and with jobId puts the job's line first, so the
  // default is the number the customer already knows. Masked calls need it too: the chosen number is
  // the caller ID.
  useEffect(() => {
    autoDialed.current = false;
    setLoaded(false);
    if (!isOpen || !softphoneSupported) return;
    setLoading(true);
    Promise.all([
      apiService.getPhoneNumbers(jobId),
      voiceManager.getActiveProvider().catch(() => 'twilio' as const),
    ])
      .then(([nums, provider]) => {
        setVoiceNumbers(nums.filter((n) => n.capabilitiesVoice && n.isActive));
        setActiveProvider(provider);
        setSelectedId('');
      })
      .catch(() => setVoiceNumbers([]))
      .finally(() => { setLoading(false); setLoaded(true); });
  }, [isOpen, softphoneSupported, jobId]);

  // Pre-warm the softphone (provider resolve + Telnyx socket login) as soon as the sheet opens, so
  // tapping "Service Strike Call" fires the invite immediately instead of waiting on a cold login.
  useEffect(() => {
    if (!isOpen || !softphoneSupported) return;
    void voiceManager.init().catch(() => { /* errors surface at call time */ });
  }, [isOpen, softphoneSupported]);

  // Close on back button / outside tap
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // The caller-ID must match the active softphone provider — a Telnyx call must dial from a Telnyx
  // number (and vice-versa). Offer the active provider's numbers; fall back to any voice number.
  const providerNumbers = voiceNumbers.filter((n) => n.provider.toLowerCase() === activeProvider);
  const choices = providerNumbers.length > 0 ? providerNumbers : voiceNumbers;
  const defaultNumber = choices.find((n) => n.id === selectedId) ?? choices[0];

  const canUseServiceStrike = softphoneSupported && !loading && !!defaultNumber &&
    (!masked || (!!jobId && !!contactId));

  async function handleServiceStrikeCall() {
    onClose();
    if (!defaultNumber) return;
    if (masked && jobId && contactId) {
      const label = displayName
        ? `${displayName} · ${toE164}`
        : toE164;
      await makeMaskedCall(jobId, contactId, contactWhich, label, defaultNumber.e164Number);
    } else if (!masked) {
      await makeCall(toE164, defaultNumber.e164Number, displayName, jobId);
    }
  }

  // A masked contact has one way to be called, so with exactly one line there is nothing to choose:
  // dial straight away, the same single tap as before this sheet handled masked calls.
  const autoDial = isOpen && masked && loaded && choices.length === 1 && canUseServiceStrike && callState === 'idle';
  useEffect(() => {
    if (!autoDial || autoDialed.current) return;
    autoDialed.current = true;
    void handleServiceStrikeCall();
  });

  // `phoneCallOnly` never renders — the effect above hands off to the phone's dialer instead.
  if (!isOpen || phoneCallOnly || autoDial) return null;

  function handlePhoneCall() {
    onClose();
    window.location.href = `tel:${toE164}`;
  }

  const label = displayName
    ? `${displayName} · ${masked ? toE164 : formatPhoneNumber(toE164)}`
    : (masked ? toE164 : formatPhoneNumber(toE164));

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9000] bg-black/40"
        onClick={onClose}
        aria-hidden
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal
        aria-label="Choose how to call"
        className="fixed bottom-0 left-0 right-0 z-[9001] max-h-[calc(100dvh-0.75rem)] overflow-y-auto rounded-t-2xl bg-white px-4 pt-4 shadow-2xl safe-area-bottom-padded"
      >
        {/* Handle */}
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-gray-300" />

        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Call</p>
            <p className="text-base font-semibold text-gray-900">{label}</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Service Strike option */}
          {softphoneSupported && (
            <button
              onClick={handleServiceStrikeCall}
              disabled={!canUseServiceStrike || callState !== 'idle'}
              className="flex w-full items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3.5 text-left transition-colors hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Phone className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {masked ? 'Service Strike Call' : (loading ? 'Checking availability…' : 'Service Strike Call')}
                </p>
                {!loading && defaultNumber && choices.length === 1 && (
                  <p className="text-xs text-gray-500">
                    Via {formatPhoneNumber(defaultNumber.e164Number)}
                  </p>
                )}
                {!loading && !defaultNumber && (
                  <p className="text-xs text-red-500">No company number is available to you</p>
                )}
              </div>
            </button>
          )}

          {/* More than one line: pick which number calls (and which the customer sees). */}
          {softphoneSupported && !loading && choices.length > 1 && (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500">Call from</span>
              <select
                value={defaultNumber?.id ?? ''}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {choices.map((n) => (
                  <option key={n.id} value={n.id}>
                    {formatVoipNumberLabel(n.e164Number, n.displayName)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* A hidden contact can't be dialed from the phone, so without a company line there is no way. */}
          {masked && (!softphoneSupported || (!loading && !defaultNumber)) && (
            <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {softphoneSupported
                ? 'No company number is available to you. Contact the office to reach this customer.'
                : 'Calling this contact is only available in the mobile app.'}
            </p>
          )}

          {/* Native phone option — hidden when masked (Twilio-only enforced) */}
          {!masked && (
            <button
              onClick={handlePhoneCall}
              className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-left transition-colors hover:bg-gray-100"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-400 text-white">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Regular Phone Call</p>
                <p className="text-xs text-gray-500">Opens your phone's dialer</p>
              </div>
            </button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
